import fastdom from 'fastdom';
import util from '#core/util';


/**
 * Breakpoint ID v20240628.1500
 */
(function(){
	const breakpoints = [600, 1000, 1300];
	const calculate = function() {
		const width = window.innerWidth;
		window.breakpoint_id = breakpoints.findIndex(breakpoint => {
			return width < breakpoint;
		});
		if (window.breakpoint_id === -1) {
			window.breakpoint_id = breakpoints.length;
		}
	};
	
	calculate();
	window.addEventListener('resize/debounced', calculate);
})('breakpoints');


/**
 * Iterate through elements once
 * @param {string} selector
 * @param {string} namespace
 * @param {function} callback
 */
function each_once(selector, namespace, callback) {
	const initialized_attr = `${namespace}-initialized`;
	const initialized_$ = `${selector}:not([${initialized_attr}])`;
	const $elements = document.querySelectorAll(initialized_$);
	for (let i = 0; i < $elements.length; i++) {
		$elements[i].setAttribute(initialized_attr, '');
		if (typeof callback === 'function') {
			callback.call($elements[i], i, $elements[i]);
		}
	}
}


// Move attributes to targets
(function(namespace){
	
	const init = function() {
		const $reveal_parents = document.querySelectorAll('[reveal-target]');
		if (!$reveal_parents.length) return;
		
		$reveal_parents.forEach($parent => {
			let $targets = [];
			const target_type = $parent.getAttribute('reveal-target');
			if (target_type === 'children') {
				$targets = $parent.children;
			} else if (target_type === 'target') {
				$targets = $parent.querySelectorAll('[reveal-target]');
			} else if (util.isValidSelector(target_type)) {
				$targets = $parent.querySelectorAll(target_type);
			}
			
			const attributes = [
				'reveal',
				'delay',
				'reveal-group',
				'aligned',
			];
			
			// Check if parent has been processed before
			const has_templates = $parent.hasAttribute('data-reveal-template');
			
			attributes.forEach(attr => {
				let value;
				
				// Get value from template or original attribute
				if (has_templates) {
					const template_attr = (attr === 'reveal')
						? 'data-reveal-template'
						: `data-reveal-template-${attr}`;
					value = $parent.getAttribute(template_attr);
				} else {
					value = $parent.getAttribute(attr);
					if (value !== null) {
						const template_attr = (attr === 'reveal')
							? 'data-reveal-template'
							: `data-reveal-template-${attr}`;
						$parent.setAttribute(template_attr, value);
					}
				}
				
				if (value === null) return false;
				
				// Apply to targets that have not been revealed
				[...$targets].forEach($target => {
					if (attr === 'reveal') {
						if (
							!$target.hasAttribute('reveal')
							&& !$target.hasAttribute('revealed')
							&& !$target.hasAttribute('revealing')
						) {
							$target.setAttribute(attr, value);
						}
					} else {
						if (!$target.hasAttribute(attr)) {
							$target.setAttribute(attr, value);
						}
					}
				});
			});
			
			// Prevent parent from animating itself
			$parent.removeAttribute('reveal');
		});
	};
	
	window.addEventListener('reinitialize', init);
	init();
})('move-reveal-to-targets');


/**
 * Reveals v20250315.1730* (* insta reveal mod)
 * Trigger animations on scroll
 * @requires wilsonpage/fastdom
 * @requires v.breakpoints
 */
(function(namespace){
	const selector = '[reveal]:not([reveal*="+"], [subreveal])';
	const margins = '1% 1% -10% 1%';
	
	// Delay values greater than this are treated as fixed delays in milliseconds
	const css_delay_count = 24;
	
	const revealed_groups = [];
	
	
	/**
	 * Check if element is in viewport, even if clipped by overflow: hidden
	 */
	const is_in_viewport = function($element) {
		if (!$element.offsetParent) return false;
		
		const rect = $element.getBoundingClientRect();
		const viewport_height = window.innerHeight || document.documentElement.clientHeight;
		const viewport_width = window.innerWidth || document.documentElement.clientWidth;
		
		return (
			rect.left < viewport_width
			&& rect.right > 0
			&& rect.top < viewport_height
			&& rect.bottom > 0
		);
	};
	
	
	/**
	 * Check if element is clipped by overflow:hidden parent
	 */
	const is_clipped_by_overflow = function($element) {
		const rect = $element.getBoundingClientRect();
		let parent = $element.parentElement;
		
		while (parent && parent !== document.body) {
			const parent_style = window.getComputedStyle(parent);
			if (
				parent_style.overflow === 'hidden'
				|| parent_style.overflowY === 'hidden'
				|| parent_style.overflowX === 'hidden'
			) {
				const parent_rect = parent.getBoundingClientRect();
				
				// Check if element is outside parent bounds
				if (
					rect.left > parent_rect.right
					|| rect.right < parent_rect.left
					|| rect.top > parent_rect.bottom
					|| rect.bottom < parent_rect.top
				) {
					return {
						is_clipped: true,
						clipping_parent: parent,
					};
				}
			}
			parent = parent.parentElement;
		}
		
		return {
			is_clipped: false,
			clipping_parent: null,
		};
	};
	
	
	const get_delay = function($element) {
		if ($element.hasAttribute('delay')) {
			const responsive_delay = $element.getAttribute('delay');
			const matches = responsive_delay.match(/^([^\/]+)?\/?([^\/]+(?=\/\/))?(?:\/{2})?((?<=\/\/)[^\/]+)?\/?([^\/]+)?$/);
			if (matches) {
				for (let i = (window.breakpoint_id || 0); i >= 0; i--) {
					if (matches[i + 1]) {
						return matches[i + 1];
					}
				}
			}
			return false;
		}
		
		const reveal_type = $element.getAttribute('reveal') || false;
		return (reveal_type)
			? (reveal_type + '').split('/')[1]
			: false;
	};
	
	const process = function($element, is_subreveal = false) {
		const delay = get_delay($element);
		if (delay && delay > css_delay_count) {
			setTimeout(() => {
				reveal({$element, is_subreveal});
			}, delay);
		} else {
			reveal({$element, is_subreveal});
		}
	};
	
	const reveal = function({$element, is_subreveal = false, is_in_group = false} = {}) {
		if ($element.hasAttribute('revealing')) return false;
		if ($element.hasAttribute('revealed')) return false;
		
		// If element is in a group, reveal all elements in the group
		if (!is_in_group) {
			const group_id = $element.getAttribute('reveal-group');
			if (group_id && !revealed_groups.includes(group_id)) {
				revealed_groups.push(group_id);
				const $group_elements = document.querySelectorAll(`[reveal-group="${group_id}"]`);
				$group_elements.forEach($group_element => {
					if ($group_element !== $element) {
						reveal({$element: $group_element, is_in_group: true});
					}
				});
			}
			
			// If element is in a group that is already being revealed, skip it
			else if (group_id && revealed_groups.includes(group_id)) {
				return false;
			}
		}
		
		fastdom.mutate(() => {
			if (!is_subreveal) {
				$element.querySelectorAll('[subreveal]').forEach($subreveal => {
					if ($subreveal.closest('[reveal]:not([subreveal])') === $element) {
						process($subreveal, true);
					}
				});
			}
			
			const reveal_type = $element.getAttribute('reveal');
			if (reveal_type === null) return false;
			
			if (
				reveal_type === ''
				|| reveal_type.startsWith('/')
				|| reveal_type.startsWith('*')
			) {
				$element.removeAttribute('reveal');
				$element.setAttribute('revealed', reveal_type);
				$element.dispatchEvent(new CustomEvent('revealed'));
			} else {
				$element.setAttribute('revealing', reveal_type);
				$element.dispatchEvent(new CustomEvent('revealing'));
				let is_transition_done = false;
				
				const revealed_callback = function() {
					if (is_transition_done) return false;
					
					is_transition_done = true;
					$element.removeAttribute('reveal');
					$element.removeAttribute('revealing');
					$element.setAttribute('revealed', reveal_type);
					$element.dispatchEvent(new CustomEvent('revealed'));
					$element.removeEventListener('transitionend', try_reveal);
				};
				
				const try_reveal = (event) => {
					if (event.target === $element) {
						revealed_callback();
					}
				};
				
				$element.addEventListener('transitionend', try_reveal);
				
				// If [reveal] is applied to an element that does not have a transition,
				// the "transitionend" event will not fire, so we need to manually mark
				// it as having been revealed
				const style = window.getComputedStyle($element);
				if (!style.transitionProperty || style.transitionProperty === 'none') {
					revealed_callback();
				}
			}
		});
	};
	
	const init = function() {
		const observer = new IntersectionObserver(function(entries, observer){
			entries.forEach(function(entry) {
				if (entry.isIntersecting) {
					process(entry.target);
					observer.unobserve(entry.target);
				}
			});
		}, { rootMargin: margins });
		
		// Immediately reveal elements already in view on page load, or observe
		setTimeout(function() {
			each_once(selector, namespace, function(index, $element) {
				const clipping_data = is_clipped_by_overflow($element);
				const rect = $element.getBoundingClientRect();
				
				if (
					$element.offsetParent
					&& (
						rect.bottom < 100
						|| (clipping_data.is_clipped && is_in_viewport($element))
					)
				) {
					reveal({$element});
				} else {
					observer.observe($element);
				}
			});
		}, 100);
		
		// TODO: test this
		each_once('[reveal^="*"]', namespace, function(index, $element) {
			process($element);
		});
	};
	
	window.addEventListener('reinitialize', init);
	init();
})('reveal');


/**
 * (v) Reveal Delays Sequencer v20240328.1945
 * Sequence elements in the same row and in groups
 */
(function(namespace){
	
	const init = function() {
		const $elements = document.querySelectorAll('[aligned]');
		const data = {};
		
		// Group elements by their "aligned" attribute and vertical position
		$elements.forEach($element => {
			if (!$element.offsetParent) return false;
			
			const bounds = $element.getBoundingClientRect();
			const group_id = $element.getAttribute('aligned') || 0;
			const row_id = `row_${Math.ceil(bounds.top)}`;
			
			data[group_id] ??= {};
			data[group_id][row_id] ??= [];
			data[group_id][row_id].push({
				$element: $element,
				left: Math.ceil(bounds.left),
			});
		});
		
		// Ignore rows with only one element
		for (const group_id in data) {
			for (const row_id in data[group_id]) {
				const row_set = data[group_id][row_id];
				if (row_set.length > 1) continue;
				
				row_set.forEach(row_item => {
					row_item.$element.removeAttribute('aligned');
				});
				delete data[group_id][row_id];
			}
		}
		
		// Set sequence order based on horizontal position
		for (const group_id in data) {
			for (const row_id in data[group_id]) {
				const row_set = data[group_id][row_id];
				row_set.sort((a, b) => a.left - b.left);
				row_set.forEach((row_item, index) => {
					row_item.$element.style.setProperty('--reveal-sequence', index);
				});
			}
		}
	};
	
	// TODO: when are these fired?
	window.addEventListener('reinitialize', init);
	window.addEventListener('recalculate', init);
	init();
	
})('aligned');


/**
 * Group elements to be revealed in a sequence
 * @todo consolidate this with "aligned"
 */
(function(namespace){
	
	const init = function() {
		const $elements = document.querySelectorAll('[reveal-group]');
		const data = {};
		
		// Group elements by their "reveal-group" attribute
		$elements.forEach($element => {
			if (!$element.offsetParent) return false;
			
			const bounds = $element.getBoundingClientRect();
			const group_id = $element.getAttribute('reveal-group') || 0;
			
			data[group_id] ??= [];
			data[group_id].push({
				$element: $element,
				left: Math.ceil(bounds.left),
				top: Math.ceil(bounds.top),
			});
		});
		
		// Ignore groups with only one element
		for (const group_id in data) {
			const group = data[group_id];
			if (group.length > 1) continue;
			
			group.forEach(group_item => {
				group_item.$element.removeAttribute('reveal-group');
			});
			delete data[group_id];
		}
		
		// Set sequence order by position
		for (const group_id in data) {
			const group = data[group_id];
			group.sort((a, b) => {
				if (a.top !== b.top) {
					return a.top - b.top;
				}
				return a.left - b.left;
			});
			group.forEach((group_item, index) => {
				group_item.$element.style.setProperty('--reveal-sequence', index);
			});
		}
	};
	
	// TODO: when are these fired?
	window.addEventListener('reinitialize', init);
	window.addEventListener('recalculate', init);
	init();
	
})('reveal-group');


// TODO: use clustering to group elements by proximity, not exact position

/**
 * Cluster values into groups based on proximity
 * @param {array} values
 * @param {int} threshold
 * @returns {array}
 * 
 * @example [21, 25, 60, 58, 64, 68, 69, 72]
 */
function cluster(values, threshold = 10) {
	const sorted = [...values].sort((a, b) => a - b);
	
	const clusters = [];
	let current_cluster = [sorted[0]];
	
	for (let i = 1; i < sorted.length; i++) {
		const previous_value = sorted[i - 1];
		const current_value = sorted[i];
		
		if (Math.abs(current_value - previous_value) <= threshold) {
			current_cluster.push(current_value);
		} else {
			clusters.push(current_cluster);
			current_cluster = [current_value];
		}
	}
	
	clusters.push(current_cluster);
	return clusters;
}
